import React from "react";
import { Box, useTheme } from "@mui/material";
import { useTranslation } from "react-i18next";
import { ClientCard, ClientPage } from "./ClientUiViewClient";
import { Bone } from "./HomeSkeletonViewClient";
import OrbLoader from "../common/OrbLoader";

// Esqueleto de una campaña o subcampaña mientras llegan los datos: migas,
// cabecera, objetivo, cifras, gráfica, inversión y la lista de subcampañas
// (solo en campaña), en el mismo sitio que la página.

/** Una tira de cifras: varias columnas con etiqueta y número. */
const StripBone: React.FC<{ count: number }> = ({ count }) => {
  const theme = useTheme();
  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: {
          xs: "repeat(2, minmax(0, 1fr))",
          md: `repeat(${count}, minmax(0, 1fr))`,
        },
        gap: 3,
        px: 3,
        py: 2.5,
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: "20px",
        backgroundColor: theme.palette.background.paper,
      }}
    >
      {Array.from({ length: count }, (_, i) => (
        <Box
          key={i}
          sx={{ display: "flex", flexDirection: "column", gap: 1.25 }}
        >
          <Bone width={72} height={12} />
          <Bone width={110} height={28} radius={8} />
        </Box>
      ))}
    </Box>
  );
};

/** Fila de la tabla de subcampañas. */
const SubRowBone: React.FC<{ nameWidth: string }> = ({ nameWidth }) => {
  const theme = useTheme();
  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns:
          "minmax(0, 1fr) 130px 160px 90px 80px 110px 132px 24px",
        columnGap: 2,
        alignItems: "center",
        px: 2.5,
        py: 2,
        borderTop: `1px solid ${theme.palette.divider}`,
      }}
    >
      <Bone width={nameWidth} height={16} />
      <Bone width={84} height={24} />
      <Bone width={140} height={10} />
      <Bone width={64} height={14} />
      <Bone width={48} height={14} />
      <Bone width={72} height={14} />
      <Bone width={96} height={24} />
      <Bone width={18} height={18} />
    </Box>
  );
};

const DetailSkeletonViewClient: React.FC<{ withSubcampaigns?: boolean }> = ({
  withSubcampaigns = false,
}) => {
  const { t } = useTranslation();
  return (
    <Box role="status" aria-busy="true" aria-label={t("clientView.loading")}>
      <ClientPage>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
          <Box sx={{ display: "flex", gap: 1.5 }}>
            <Bone width={70} height={14} />
            <Bone width={140} height={14} />
            {!withSubcampaigns && <Bone width={120} height={14} />}
          </Box>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
            <Bone width="min(420px, 80%)" height={34} radius={10} />
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2.5 }}>
              <Bone width={96} height={24} />
              <Bone width={96} height={24} />
              <Bone width={170} height={14} />
              <Bone width={130} height={14} />
            </Box>
          </Box>
        </Box>

        <ClientCard>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <Bone width={200} height={16} />
            <Bone width={160} height={40} radius={10} />
            <Bone height={12} />
          </Box>
        </ClientCard>

        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          <StripBone count={4} />
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "minmax(0, 1fr)",
                lg: "minmax(0, 5fr) minmax(0, 7fr)",
              },
              gap: 2,
            }}
          >
            <StripBone count={2} />
            <StripBone count={3} />
          </Box>
        </Box>

        <ClientCard
          title={<Bone width={220} height={18} />}
          action={<Bone width={180} height={36} />}
        >
          <Bone height={300} radius={16} />
        </ClientCard>

        <ClientCard title={<Bone width={160} height={18} />}>
          <Bone height={180} radius={16} />
        </ClientCard>

        {withSubcampaigns && (
          <ClientCard title={<Bone width={140} height={18} />}>
            <Box sx={{ overflowX: "auto" }}>
              <Box sx={{ minWidth: 960 }}>
                {["58%", "44%", "52%"].map((width) => (
                  <SubRowBone key={width} nameWidth={width} />
                ))}
              </Box>
            </Box>
          </ClientCard>
        )}
      </ClientPage>
    </Box>
  );
};

/** Hueco de los apartados de detalle mientras llegan: un orbe en su tarjeta. */
export const DetailSectionsLoading: React.FC = () => {
  const { t } = useTranslation();
  return (
    <ClientCard>
      <OrbLoader size="medium" centered label={t("clientView.loading")} />
    </ClientCard>
  );
};

export default DetailSkeletonViewClient;
